import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Truck, Bus, GraduationCap } from "lucide-react";
import { Reveal, easeOut } from "@/components/common/Reveal";
import { DOMAINES } from "@/data/site";

const ICONS = {
  "transport-de-marchandises": Truck,
  "transport-de-voyageurs": Bus,
  "formations-enseignants": GraduationCap,
};

export default function Filieres() {
  const [active, setActive] = useState(null);

  return (
    <section className="bg-brand-blue py-24 lg:py-32" data-testid="filieres">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
        <Reveal className="max-w-2xl">
          <h2 className="font-display text-4xl font-black uppercase leading-[0.98] tracking-[-0.03em] text-white sm:text-5xl lg:text-[3.4rem]">
            Nos filières d'expertise
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-white/70">
            Construisez votre parcours professionnel grâce à nos formations certifiantes. Du permis
            au titre professionnel, formez-vous dans nos centres d'Île-de-France, en présentiel.
          </p>
          <p className="mt-4 font-display text-base font-bold uppercase tracking-tight text-white">
            Pour devenir un pro, c'est par ici !
          </p>
        </Reveal>

        {/* List + hover image preview */}
        <div className="relative mt-14">
          {/* Floating image (desktop) — fixed, vertically centered in the empty area */}
          <div className="pointer-events-none absolute right-0 top-1/2 z-10 hidden h-[300px] w-[420px] -translate-y-1/2 lg:block">
            <AnimatePresence>
              {active !== null && (
                <motion.div
                  key={active}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.35, ease: easeOut }}
                  className="h-full w-full overflow-hidden rounded-3xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)]"
                >
                  <img
                    src={DOMAINES[active].image}
                    alt={DOMAINES[active].title}
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/40 to-transparent" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="border-b border-white/15 lg:pr-[500px]" onMouseLeave={() => setActive(null)}>
            {DOMAINES.map((d, i) => (
              <motion.div
                key={d.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, ease: easeOut, delay: i * 0.08 }}
              >
                <Link
                  to={`/nos-formations/${d.slug}`}
                  onMouseEnter={() => setActive(i)}
                  className="group flex items-center gap-5 border-t border-white/15 py-6 sm:gap-8 lg:py-8"
                  data-testid={`filiere-${d.slug}`}
                >
                  <span className="w-8 shrink-0 font-display text-lg font-bold text-white/40 transition-colors group-hover:text-brand-red-light sm:w-12 sm:text-2xl">
                    0{i + 1}
                  </span>
                  <h3 className="flex-1 font-display text-2xl font-black uppercase leading-[1.2] tracking-[-0.02em] text-white/25 transition-colors duration-300 group-hover:text-white sm:text-3xl lg:text-[2.6rem]">
                    {d.title}
                  </h3>
                  {/* Mobile / tablet: sigle (icône) en rapport avec le titre */}
                  {(() => {
                    const Ico = ICONS[d.slug] || Truck;
                    return (
                      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white/10 text-brand-red-light sm:h-16 sm:w-16 lg:hidden">
                        <Ico className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={1.6} />
                      </span>
                    );
                  })()}
                  <ArrowUpRight className="hidden h-9 w-9 shrink-0 text-white/40 transition-all duration-300 group-hover:translate-x-1 group-hover:text-brand-red-light lg:block" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
